import React, { useState, useEffect, useCallback } from 'react';
import { farmsApi, organizationsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import FarmMap from '../components/map/FarmMap';
import LocationPickerWithBoundary from '../components/map/LocationPickerWithBoundary';
import { MapPin, Plus, MoreHorizontal, Pencil, Trash2, Loader2, Ruler, Navigation, Map, LayoutGrid, Building2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const FarmsPage = () => {
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFarm, setEditingFarm] = useState(null);
  const [viewMode, setViewMode] = useState('cards');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    total_area: '',
    latitude: '',
    longitude: '',
    boundary: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const { isAdmin, isSuperAdmin } = useAuth();

  const handleLocationChange = useCallback((lat, lng) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6),
    }));
  }, []);

  const handleBoundaryChange = useCallback((boundary) => {
    setFormData(prev => ({
      ...prev,
      boundary: boundary,
    }));
  }, []);

  const handleAreaCalculated = useCallback((area) => {
    setFormData(prev => ({
      ...prev,
      total_area: area.toFixed(2),
    }));
  }, []);

  const fetchFarms = async () => {
    try {
      const [farmsRes, orgsRes] = await Promise.all([
        farmsApi.getAll(),
        organizationsApi.getAll().catch(() => ({ data: [] })),
      ]);
      setFarms(farmsRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des exploitations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFarms();
  }, []);

  const handleOpenDialog = (farm = null) => {
    if (farm) {
      setEditingFarm(farm);
      setFormData({
        name: farm.name,
        description: farm.description || '',
        address: farm.address || '',
        total_area: farm.total_area?.toString() || '',
        latitude: farm.latitude?.toString() || '',
        longitude: farm.longitude?.toString() || '',
        boundary: farm.boundary || [],
        organization_id: farm.organization_id || '',
      });
    } else {
      setEditingFarm(null);
      setFormData({
        name: '',
        description: '',
        address: '',
        total_area: '',
        latitude: '',
        longitude: '',
        boundary: [],
        organization_id: organizations[0]?.id || '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const data = {
      name: formData.name,
      description: formData.description || null,
      address: formData.address || null,
      total_area: formData.total_area ? parseFloat(formData.total_area) : null,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      organization_id: formData.organization_id || null,
    };

    try {
      if (editingFarm) {
        await farmsApi.update(editingFarm.id, data);
        toast.success('Exploitation mise à jour');
      } else {
        await farmsApi.create(data);
        toast.success('Exploitation créée');
      }
      setDialogOpen(false);
      fetchFarms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette exploitation ?')) return;

    try {
      await farmsApi.delete(id);
      toast.success('Exploitation supprimée');
      fetchFarms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="farms-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Exploitations</h1>
          <p className="text-muted-foreground mt-1">Gérez vos exploitations agricoles</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList>
              <TabsTrigger value="cards" data-testid="view-cards">
                <LayoutGrid className="h-4 w-4" />
              </TabsTrigger>
              <TabsTrigger value="map" data-testid="view-map">
                <Map className="h-4 w-4" />
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {isAdmin && (
            <Button onClick={() => handleOpenDialog()} data-testid="add-farm-btn">
              <Plus className="mr-2 h-4 w-4" />
              Nouvelle exploitation
            </Button>
          )}
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && (
        <Card className="h-[calc(100vh-180px)]" data-testid="farms-map-view">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Map className="h-5 w-5 text-primary" />
              Carte des exploitations
            </CardTitle>
            <CardDescription>
              Cliquez sur un marqueur pour voir les détails
            </CardDescription>
          </CardHeader>
          <CardContent className="h-[calc(100%-80px)] pb-4">
            <FarmMap 
              farms={farms} 
              height="100%"
              onFarmClick={(farm) => handleOpenDialog(farm)}
            />
          </CardContent>
        </Card>
      )}

      {/* Cards Grid */}
      {viewMode === 'cards' && (
        <>
          {farms.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Aucune exploitation</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {farms.map((farm) => (
                <Card key={farm.id} className="card-hover" data-testid={`farm-card-${farm.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                          <MapPin className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{farm.name}</CardTitle>
                          {farm.address && (
                            <CardDescription className="text-sm mt-0.5">
                              {farm.address}
                            </CardDescription>
                          )}
                        </div>
                      </div>
                      {isAdmin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`farm-menu-${farm.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenDialog(farm)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleDelete(farm.id)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {farm.description && (
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {farm.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-sm">
                      {farm.total_area && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Ruler className="h-4 w-4" />
                          <span>{farm.total_area} ha</span>
                        </div>
                      )}
                      {farm.latitude && farm.longitude && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Navigation className="h-4 w-4" />
                          <span>{farm.latitude.toFixed(4)}, {farm.longitude.toFixed(4)}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[90vw] max-w-[90vw] h-[85vh] max-h-[85vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-xl">
              {editingFarm ? 'Modifier l\'exploitation' : 'Nouvelle exploitation'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto pr-2">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
                {/* Left column - Form fields */}
                <div className="space-y-4">
                  {isSuperAdmin && !editingFarm && (
                    <div className="space-y-2">
                      <Label htmlFor="organization_id" className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Organisation *
                      </Label>
                      <Select
                        value={formData.organization_id}
                        onValueChange={(v) => setFormData({ ...formData, organization_id: v })}
                        required
                      >
                        <SelectTrigger data-testid="farm-org-select">
                          <SelectValue placeholder="Sélectionner une organisation" />
                        </SelectTrigger>
                        <SelectContent>
                          {organizations.map((org) => (
                            <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom de l'exploitation *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Domaine des Oliviers"
                        required
                        data-testid="farm-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="total_area">Surface (hectares)</Label>
                      <Input
                        id="total_area"
                        type="number"
                        step="0.01"
                        value={formData.total_area}
                        onChange={(e) => setFormData({ ...formData, total_area: e.target.value })}
                        placeholder="Calculée automatiquement"
                        data-testid="farm-area-input"
                      />
                      {formData.boundary?.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Surface calculée depuis le tracé
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address">Adresse</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="123 Route des Vignes, 34000 Montpellier"
                      data-testid="farm-address-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Description de l'exploitation, types de cultures, particularités..."
                      rows={3}
                      data-testid="farm-description-input"
                    />
                  </div>

                  {/* Coordinates display */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="latitude">Latitude</Label>
                      <Input
                        id="latitude"
                        type="number"
                        step="0.000001"
                        value={formData.latitude}
                        onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                        placeholder="43.6108"
                        data-testid="farm-latitude-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="longitude">Longitude</Label>
                      <Input
                        id="longitude"
                        type="number"
                        step="0.000001"
                        value={formData.longitude}
                        onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                        placeholder="3.8767"
                        data-testid="farm-longitude-input"
                      />
                    </div>
                  </div>

                  {/* Boundary info */}
                  {formData.boundary?.length > 0 && (
                    <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 p-3">
                      <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                        <MapPin className="h-4 w-4" />
                        <span className="font-medium">Limites définies</span>
                      </div>
                      <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-1">
                        Périmètre tracé avec {formData.boundary.length} points
                      </p>
                    </div>
                  )}
                </div>

                {/* Right column - Map */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Map className="h-4 w-4" />
                    Emplacement et limites sur la carte
                  </Label>
                  <LocationPickerWithBoundary
                    latitude={formData.latitude ? parseFloat(formData.latitude) : null}
                    longitude={formData.longitude ? parseFloat(formData.longitude) : null}
                    boundary={formData.boundary}
                    onLocationChange={handleLocationChange}
                    onBoundaryChange={handleBoundaryChange}
                    onAreaCalculated={handleAreaCalculated}
                    height="calc(85vh - 280px)"
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter className="shrink-0 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={submitting} data-testid="farm-submit-btn">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingFarm ? 'Mettre à jour' : 'Créer l\'exploitation'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FarmsPage;
